import { useEffect, useMemo, useState } from 'react';
import { FiCheck, FiClock, FiRefreshCw, FiRotateCcw, FiXCircle } from 'react-icons/fi';
import { TableHeader, TablePerPage } from '../../../../../_CORE/APP/Templates/_UI/Table/_index';
import { FooterPagination } from '../../../../../_CORE/APP/Templates/Layouts/Section/Table/_Plugins/FooterPagination';
import { EmailSectionLayout } from '../_UI/EmailSectionLayout';
import { useEmailAdmin } from '../_Hooks/useEmailAdmin';
import { EMAIL_ADMIN_CONSTANTS as C } from '../_MODULES/_API';
import { confirm } from '../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost';

const norm = (v = '') => String(v ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
const statusLabel = (value) => ({ queued: 'Por enviar', processing: 'Procesando', sent: 'Enviado', failed: 'Fallido', skipped: 'Omitido', suppressed: 'Bloqueado' }[value] || value);
const dateValue = (value) => value ? new Date(value) : null;
const dayKey = (value) => {
    const date = value instanceof Date ? value : dateValue(value);
    if (!date || Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
};

export const EmailQueueView = () => {
    const api = useEmailAdmin();
    const [items, setItems] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [message, setMessage] = useState('');
    const [search, setSearch] = useState('');
    const [filters, setFilters] = useState({ status: null, campaign: null, date: null, range: [null, null] });
    const [order, setOrder] = useState('updated-desc');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(12);

    const load = async () => {
        try {
            const [recipients, campaignList] = await Promise.all([api.get(C.RECIPIENTS), api.get(C.CAMPAIGNS)]);
            setItems(recipients || []); setCampaigns(campaignList || []); setMessage('');
        } catch (err) { setMessage(err.message); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    const filtered = useMemo(() => {
        const term = norm(search);
        const selectedStatus = filters.status?.value ?? filters.status;
        const selectedCampaign = filters.campaign?.value ?? filters.campaign;
        const exact = filters.date ? dayKey(filters.date) : '';
        const [from, to] = filters.range || [null, null];
        const fromTime = from instanceof Date ? new Date(from.getFullYear(), from.getMonth(), from.getDate()).getTime() : null;
        const toTime = to instanceof Date ? new Date(to.getFullYear(), to.getMonth(), to.getDate(), 23, 59, 59, 999).getTime() : null;

        const rows = items.filter((item) => {
            if (term && !norm(`${item.email} ${item.first_name} ${item.last_name} ${item.company} ${item.campaign_name} ${item.campaign_subject}`).includes(term)) return false;
            if (selectedStatus && item.status !== selectedStatus) return false;
            if (selectedCampaign && String(item.campaign_id) !== String(selectedCampaign)) return false;
            const recordDate = item.sent_at || item.failed_at || item.queued_at || item.created_at;
            if (exact && dayKey(recordDate) !== exact) return false;
            if (fromTime || toTime) {
                const current = dateValue(recordDate)?.getTime();
                if (!current) return false;
                if (fromTime && current < fromTime) return false;
                if (toTime && current > toTime) return false;
            }
            return true;
        });

        return [...rows].sort((a, b) => {
            if (order === 'email-asc') return String(a.email).localeCompare(String(b.email));
            if (order === 'campaign-asc') return String(a.campaign_name).localeCompare(String(b.campaign_name));
            return new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at);
        });
    }, [items, filters, search, order]);

    useEffect(() => { setPage(1); }, [search, filters, order, perPage]);
    const totalPages = perPage === -1 ? 1 : Math.max(1, Math.ceil(filtered.length / perPage));
    const safePage = Math.min(page, totalPages);
    const visible = perPage === -1 ? filtered : filtered.slice((safePage - 1) * perPage, safePage * perPage);
    const from = filtered.length ? (perPage === -1 ? 1 : ((safePage - 1) * perPage) + 1) : 0;
    const to = filtered.length ? (perPage === -1 ? filtered.length : Math.min(safePage * perPage, filtered.length)) : 0;

    const counts = useMemo(() => ({
        queued: items.filter((x) => ['queued','processing'].includes(x.status)).length,
        sent: items.filter((x) => x.status === 'sent').length,
        failed: items.filter((x) => x.status === 'failed').length,
    }), [items]);

    const retryFailed = async () => {
        if (!counts.failed) return;
        if (!(await confirm.warning(`Reintentar ${counts.failed} envios fallidos?`, 'Los destinatarios fallidos volveran a la cola.'))) return;
        try {
            const result = await api.securePost(C.CAMPAIGN_RETRY_FAILED, {});
            setMessage(`${result.retried || 0} destinatarios regresaron a la cola.`);
            await load();
        } catch (err) { setMessage(err.message); }
    };

    return (
        <EmailSectionLayout
            title="Cola e historial"
            description="Controla qué correos están por enviar, cuáles salieron y cuáles fallaron."
            actions={<div className="PBMAIL__header_actions"><button type="button" className="PBADMIN__secondary PBMAIL__refresh" onClick={load}><FiRefreshCw /> Actualizar</button>{counts.failed > 0 && <button type="button" className="PBADMIN__secondary PBMAIL__refresh" onClick={retryFailed}><FiRotateCcw /> Reintentar fallidos</button>}</div>}
        >
            <div className="PBMAIL__queue_summary">
                <div><FiClock /><strong>{counts.queued}</strong><span>Por enviar</span></div>
                <div><FiCheck /><strong>{counts.sent}</strong><span>Enviados</span></div>
                <div><FiXCircle /><strong>{counts.failed}</strong><span>Fallidos</span></div>
            </div>

            <section className="PBADMIN__panel PBMAIL__panel">
                <div className="PBADMIN__collection_tools PBMAIL__collection_tools">
                    <TableHeader
                        pill="SEGUIMIENTO"
                        title="Historial de"
                        titleAccent="destinatarios"
                        hasNew={false}
                        search={search}
                        onSearch={setSearch}
                        searchPlaceholder="Buscar email, persona, empresa o campaña…"
                        orderOptions={[{ value: 'updated-desc', label: 'Actividad reciente' }, { value: 'email-asc', label: 'Email A → Z' }, { value: 'campaign-asc', label: 'Campaña A → Z' }]}
                        order={order}
                        onOrder={setOrder}
                        filters={[
                            { key: 'status', label: 'Estado', type: 'select', value: filters.status, onChange: (v) => setFilters((p) => ({ ...p, status: v })), placeholder: 'Todos', options: [
                                { value: 'queued', label: 'Por enviar' }, { value: 'processing', label: 'Procesando' }, { value: 'sent', label: 'Enviado' }, { value: 'failed', label: 'Fallido' }, { value: 'skipped', label: 'Omitido' }, { value: 'suppressed', label: 'Bloqueado' },
                            ] },
                            { key: 'campaign', label: 'Campaña', type: 'select', value: filters.campaign, onChange: (v) => setFilters((p) => ({ ...p, campaign: v })), placeholder: 'Todas', options: campaigns.map((c) => ({ value: c.id, label: c.name })) },
                            { key: 'date', label: 'Fecha exacta', type: 'date', value: filters.date, onChange: (v) => setFilters((p) => ({ ...p, date: v })) },
                            { key: 'range', label: 'Rango de fechas', type: 'dateRange', col: 2, value: filters.range, onChange: (v) => setFilters((p) => ({ ...p, range: v || [null, null] })) },
                        ]}
                        onClearFilters={() => { setSearch(''); setFilters({ status: null, campaign: null, date: null, range: [null, null] }); }}
                        stats={<div className="PBADMIN__filter_summary"><span><strong>{filtered.length}</strong> resultado{filtered.length === 1 ? '' : 's'}</span><span>{items.length} total</span></div>}
                    />
                </div>

                {message && <div className="PBADMIN__alert">{message}</div>}

                <div className="PBMAIL__recipient_table">
                    <div className="PBMAIL__recipient_head"><span>Destinatario</span><span>Campaña</span><span>Estado</span><span>Intentos</span><span>Fecha</span></div>
                    {visible.length === 0 && !api.isLoading && <div className="PBADMIN__empty">No hay envíos con los filtros actuales.</div>}
                    {visible.map((item) => (
                        <article key={item.id} className="PBMAIL__recipient_row">
                            <div><strong>{[item.first_name, item.last_name].filter(Boolean).join(' ') || item.email}</strong><span>{item.email}</span><small>{item.company || 'Sin empresa'}</small></div>
                            <div><strong>{item.campaign_name}</strong><span>{item.campaign_subject}</span></div>
                            <div><span className={`PBMAIL__status is-${item.status}`}>{statusLabel(item.status)}</span>{item.error_message && <small className="PBMAIL__error_text" title={item.error_message}>{item.error_message}</small>}</div>
                            <div><strong>{item.attempts}</strong></div>
                            <div><span>{item.sent_at || item.failed_at || item.queued_at || item.created_at}</span></div>
                        </article>
                    ))}
                </div>

                {filtered.length > 0 && <div className="PBADMIN__collection_footer"><div className="PBADMIN__collection_count">Mostrando <strong>{from}–{to}</strong> de <strong>{filtered.length}</strong></div><TablePerPage value={perPage} onChange={setPerPage} totalItems={filtered.length} /><FooterPagination pagina={safePage} totalPaginas={totalPages} onIr={setPage} /></div>}
            </section>
        </EmailSectionLayout>
    );
};
